import { aawzDate } from '@utils/dates/aawz-date'
import {
  MONTHS_LONG,
  MONTHS_SHORT,
  monthIndexFromName,
  monthNameFromIndex,
} from '@modules/controladoria-v2/constants/Months'

/**
 * formatMonthYear({mes, ano}) -> "Março - 2026"
 * Accepts `mes` as either the long month name ("Março") or the 0-based numeric index.
 */
export const formatMonthYear = ({ mes, ano } = {}) => {
  if (mes === undefined || mes === null || ano === undefined || ano === null) {
    return ''
  }
  const monthName =
    typeof mes === 'number' ? monthNameFromIndex(mes) : String(mes)
  if (!monthName) return ''
  return `${monthName} - ${ano}`
}

/**
 * parseMonthYear("Março - 2026") -> { mes: "Março", ano: "2026", monthIndex: 2 }
 * Returns null if the string can't be parsed.
 */
export const parseMonthYear = (str) => {
  if (typeof str !== 'string' || !str.trim()) return null
  const parts = str.split(' - ')
  if (parts.length !== 2) return null
  const monthName = parts[0].trim()
  const ano = parts[1].trim()
  if (!monthName || !ano) return null
  const monthIndex = monthIndexFromName(monthName)
  if (monthIndex < 0) return null
  return { mes: monthName, ano, monthIndex }
}

/**
 * monthKey({mes, ano}) -> "2026-2"
 * Used as Set keys by the month-grid component (Phase 7).
 * `mes` may be either the numeric 0-based index or the long month name.
 */
export const monthKey = ({ mes, ano } = {}) => {
  if (mes === undefined || mes === null || ano === undefined || ano === null) {
    return ''
  }
  const idx = typeof mes === 'number' ? mes : monthIndexFromName(mes)
  if (idx < 0) return ''
  return `${ano}-${idx}`
}

/**
 * formatMonthYearShort({mes:0, ano:2026}) -> "Jan/2026"
 */
export const formatMonthYearShort = ({ mes, ano } = {}) => {
  if (mes === undefined || mes === null || ano === undefined || ano === null) {
    return ''
  }
  const idx = typeof mes === 'number' ? mes : monthIndexFromName(mes)
  if (idx < 0 || idx > 11) return ''
  return `${MONTHS_SHORT[idx]}/${ano}`
}

/**
 * Formats a Date as "dd/MM/yyyy HH:mm" (used to stamp engine.lastRun).
 */
export const formatLastRun = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  return aawzDate(d).format('DD/MM/YYYY HH:mm')
}

/**
 * Returns the long month name for a given numeric index (mirrors monthNameFromIndex with
 * a safe default — useful for templates that may receive `null`/`undefined`).
 */
export const safeMonthName = (idx) => {
  if (typeof idx !== 'number') return ''
  if (idx < 0 || idx >= MONTHS_LONG.length) return ''
  return MONTHS_LONG[idx]
}

/**
 * dataReferenciaFromMesAno({mes, ano}) -> "01/MM/YYYY".
 *
 * Canonical wire format used by the backend (parser_date_dayfirst_v2). The day is always
 * "01" since `controladoria-v2` operates at month granularity; the backend then normalises
 * to last-day-of-month internally via `offset_to_last_day_on_month`.
 *
 * `mes` accepts either a 0-based numeric index (matching `formatMonthYear` / `monthKey`
 * conventions inside this module) or a Portuguese long-month name ("Março").
 *
 * Returns '' for invalid input.
 */
export const dataReferenciaFromMesAno = ({ mes, ano } = {}) => {
  if (mes === undefined || mes === null || ano === undefined || ano === null) {
    return ''
  }
  const idx = typeof mes === 'number' ? mes : monthIndexFromName(mes)
  if (idx < 0 || idx > 11) return ''
  const anoNum = typeof ano === 'number' ? ano : Number.parseInt(String(ano), 10)
  if (!Number.isFinite(anoNum) || anoNum <= 0) return ''
  const mm = String(idx + 1).padStart(2, '0')
  const yyyy = String(anoNum).padStart(4, '0')
  return `01/${mm}/${yyyy}`
}

/**
 * mesAnoFromDataReferencia("01/03/2026") -> { mes: "Março", ano: 2026, monthIndex: 2 }.
 *
 * Inverse of `dataReferenciaFromMesAno`. The shape mirrors `parseMonthYear` (which parses
 * the human-readable "Março - 2026" display string), so existing consumers that read
 * `{ mes, ano, monthIndex }` keep working.
 *
 * Returns null for invalid input. `ano` is returned as a number (parseMonthYear returns
 * a string) so it can flow directly into computations or comparisons.
 */
export const mesAnoFromDataReferencia = (dataRef) => {
  if (typeof dataRef !== 'string' || !dataRef.trim()) return null
  const parsed = aawzDate(dataRef, 'DD/MM/YYYY')
  if (!parsed.isValid()) return null
  const monthIndex = parsed.month()
  const ano = parsed.year()
  const mes = monthNameFromIndex(monthIndex)
  if (!mes) return null
  return { mes, ano, monthIndex }
}

/**
 * formatDataReferenciaDisplay("01/03/2026") -> "Março - 2026" (default).
 * formatDataReferenciaDisplay("01/03/2026", 'short') -> "Mar/2026".
 *
 * Convenience wrapper that goes data_referencia -> human display in one call.
 * Returns '' for invalid input so it is template-safe.
 */
export const formatDataReferenciaDisplay = (dataRef, kind = 'long') => {
  const parts = mesAnoFromDataReferencia(dataRef)
  if (!parts) return ''
  return kind === 'short'
    ? formatMonthYearShort({ mes: parts.monthIndex, ano: parts.ano })
    : formatMonthYear({ mes: parts.monthIndex, ano: parts.ano })
}

/**
 * lastDayFromMesAno({mes, ano}) -> "DD/MM/YYYY" (last day of the given month).
 *
 * Used by the controladoria-v2 fechamento DRE wrapper to lock the date range to
 * the selected month — `startDate = endDate = last day of :mes/:ano`. Builds the
 * first-of-month string via `dataReferenciaFromMesAno`, then derives the last day
 * directly via `new Date(year, month, 0)` (day 0 of the next month) so leap years
 * and 30/31-day variations are handled correctly.
 *
 * NOTE: we intentionally do NOT round-trip through `aawzDate().endOf('month')`.
 * `endOf('month')` sets the time to 23:59:59.999 (local) while `format` reads UTC
 * components — in negative-offset timezones (e.g. UTC-3) that rolls the day over
 * to the 1st of the next month. The local-Date arithmetic below is timezone-safe
 * (construct and read both use local components, anchored at midnight).
 *
 * Returns '' for invalid input.
 */
export const lastDayFromMesAno = ({ mes, ano } = {}) => {
  const dataRef = dataReferenciaFromMesAno({ mes, ano })
  if (!dataRef) return ''
  const [, mm, yyyy] = dataRef.split('/')
  // day 0 of the following month = last day of the target month (local, TZ-safe)
  const lastDay = new Date(Number(yyyy), Number(mm), 0).getDate()
  return `${String(lastDay).padStart(2, '0')}/${mm}/${yyyy}`
}

/**
 * Comparador cronológico para chaves `DD/MM/YYYY` (dia sempre 01): ano vence
 * mês. Uso: `chaves.sort(compareDataReferencia)`.
 */
export const compareDataReferencia = (a, b) => {
  const [, mesA, anoA] = a.split('/')
  const [, mesB, anoB] = b.split('/')
  return Number(anoA) - Number(anoB) || Number(mesA) - Number(mesB)
}
